import React from 'react';
import { View, Heading, Text, Button, Flex, Alert, IconArrowOpenDownLine, IconArrowOpenUpLine, IconLikeLine } from '@smarterservices/smarter-ui';

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the VTC component tree and displays
 * a fallback UI instead of crashing the entire application with a white screen.
 * 
 * Features:
 * - Catches errors during rendering, in lifecycle methods, and in constructors
 * - Logs error details to console (can be extended to send to error tracking service)
 * - Provides user-friendly error message with recovery options
 * - Allows users to reload the page or go back
 * - Shows error details in development mode
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorCount: 0,
            showErrorDetails: false,
            copiedToClipboard: false
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Update state with error details
        this.setState(prevState => ({
            error,
            errorInfo,
            errorCount: prevState.errorCount + 1
        }));

        // TODO: Send error to logging service
        // Example: logErrorToService(error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoBack = () => {
        window.history.back();
    };

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    toggleErrorDetails = () => {
        this.setState(prevState => ({
            showErrorDetails: !prevState.showErrorDetails
        }));
    };

    copyErrorDetails = async () => {
        const { error, errorInfo } = this.state;

        // Format error details for clipboard
        const errorText = [
            'Error Details:',
            '',
            'Error Message:',
            error?.toString() || 'Unknown error',
            '',
            'Component Stack:',
            errorInfo?.componentStack || 'No stack trace available',
            '',
            'Timestamp:',
            new Date().toISOString(),
            '',
            'User Agent:',
            navigator.userAgent
        ].join('\n');

        try {
            // Use modern Clipboard API
            await navigator.clipboard.writeText(errorText);

            // Show feedback
            this.setState({ copiedToClipboard: true });

            // Reset feedback after 2 seconds
            setTimeout(() => {
                this.setState({ copiedToClipboard: false });
            }, 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = errorText;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();

            try {
                document.execCommand('copy');
                this.setState({ copiedToClipboard: true });
                setTimeout(() => {
                    this.setState({ copiedToClipboard: false });
                }, 2000);
            } catch (fallbackErr) {
                console.error('Failed to copy error details:', fallbackErr);
            }

            document.body.removeChild(textArea);
        }
    };

    render() {
        if (this.state.hasError) {
            const isDevelopment = process.env.NODE_ENV === 'development';

            return (
                <View
                    as="div"
                    padding="large"
                    textAlign="center"
                    display="flex"
                    margin="auto none"
                    style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5'
                    }}
                >
                    <View
                        as="div"
                        maxWidth="600px"
                        background="primary"
                        padding="large"
                        borderRadius="medium"
                        borderColor="primary"
                        borderWidth="small"
                        shadow="resting"
                        margin="none auto"
                    >
                        <Heading level="h2" margin="none none medium none">
                            <span style={{ display: 'block', transform: 'rotate(180deg)', marginBottom: '1rem' }}>
                                <IconLikeLine size="medium" />
                            </span>
                            {' '}Something went wrong
                        </Heading>

                        <Alert
                            variant="error"
                            margin="none none medium none"
                        >
                            The application encountered an unexpected error. This has been logged and will be reviewed.
                        </Alert>

                        <Text as="p" margin="none none medium none">
                            Try reloading the page or going back to the previous page.
                        </Text>

                        <Flex gap="small" justifyItems="center" margin="small none medium none">
                            <Flex.Item>
                                <Button onClick={this.handleReload} color="primary">
                                    Reload Page
                                </Button>
                            </Flex.Item>
                            <Flex.Item>
                                <Button onClick={this.handleGoBack} color="secondary">
                                    Go Back
                                </Button>
                            </Flex.Item>
                            {isDevelopment && (
                                <Flex.Item>
                                    <Button onClick={this.handleReset} color="secondary">
                                        Try Again
                                    </Button>
                                </Flex.Item>
                            )}
                        </Flex>

                        {/* Show error details in development mode */}
                        {isDevelopment && this.state.error && (
                            <View as="div" margin="large none none none">
                                <Button
                                    onClick={this.toggleErrorDetails}
                                    color="secondary"
                                    display="block"
                                    size="small"
                                >
                                    {this.state.showErrorDetails ? <IconArrowOpenUpLine /> : <IconArrowOpenDownLine />}
                                    <View margin="none xx-small">{this.state.showErrorDetails ? 'Hide' : 'View'} Error Details</View>
                                </Button>

                                {this.state.showErrorDetails && (
                                    <View
                                        as="div"
                                        textAlign="end"
                                        margin="small none none none"
                                        padding="small"
                                        maxHeight="300px"
                                        overflowX="auto"
                                    >
                                        <Button
                                            onClick={this.copyErrorDetails}
                                            size="small"
                                            color={this.state.copiedToClipboard ? "success" : "primary"}
                                        >
                                            {this.state.copiedToClipboard ? 'Copied!' : 'Copy Error Details'}
                                        </Button>
                                        <View
                                            as="div"
                                            textAlign="start"
                                            margin="small none none none"
                                            padding="small"
                                            background="secondary"
                                            borderRadius="small"
                                            overflowX="auto"
                                        >
                                            <Text weight="bold" display="block" margin="none none small none">
                                                Error Details:{' '}
                                            </Text>
                                            <Text display="block" color="danger" margin="none none small none">
                                                {this.state.error.toString()}
                                            </Text>
                                            {this.state.errorInfo && (
                                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {this.state.errorInfo.componentStack}
                                                </pre>
                                            )}
                                        </View>
                                    </View>
                                )}
                            </View>
                        )}

                        {this.state.errorCount > 1 && (
                            <Text as="p" size="small" margin="medium none none none" color="secondary">
                                Error occurred {this.state.errorCount} times
                            </Text>
                        )}
                    </View>
                </View>

            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
